// =============================================================================
// Creative Performance Dashboard — shared types, metric helpers, and per-brand
// config parsing (Task: master-gated per-brand Creative Performance Dashboard).
//
// Data comes from the external key-protected analytics API
// (https://analytics.personaos.com/api/external). The API is
// brand-agnostic: each brand has its own key (sent as X-API-Key) and a key can
// only return its own brand's data. The raw key NEVER reaches the client — the
// brand config stores only a *reference* to a server-side secret
// (CREATIVE_API_KEY_<BRAND>), and all calls go through the server proxy.
//
// Metric rule (§2 of the rebuild guide): every ratio is computed from SUMMED
// raw counts across the slice — never by averaging per-ad ratios. These
// helpers are the single implementation used by the dashboard client and are
// unit-tested server-side.
// =============================================================================

import { parsePersonaOSAdName } from "./ad-naming";

export const CREATIVE_TIERS = ["Cold", "Warm", "Retargeting", "Legacy", "All"] as const;
export type CreativeTier = (typeof CREATIVE_TIERS)[number];

export type CreativeThumbnailSource =
  | "creativeImageUrl"
  | "thumbnailUrl"
  | "imageUrl"
  | "none";

export interface CreativeAdRow {
  adId: string;
  adName: string;
  campaignId: string;
  campaign: string;
  // Server-derived canonical Meta Ads Manager destination. It is omitted
  // unless both the configured account id and this row's campaign id are
  // valid Meta numeric ids; clients must never construct this URL themselves.
  campaignAdsManagerUrl?: string;
  tier: "Cold" | "Warm" | "Retargeting" | "Legacy";
  producer: string; // "Persona" = PersonaOS-made creative; else the brand's in-house label
  format: "Video" | "Static";
  persona?: string;
  product: string;
  batch?: string;
  isBoosted: boolean;
  spend: number;
  impressions: number;
  reach: number;
  clicks: number; // ALL clicks — CTR is an all-click CTR (footnoted in UI)
  atc: number;
  checkout: number;
  purchases: number;
  revenue: number;
  // Dynamic lead count (Task 2363): populated by the feed only when the
  // proxy passes a `leadEvent` param (the brand's configured Meta lead
  // event). Absent rows sum to 0.
  leads?: number;
  firstDate: string;
  lastDate: string;
  thumbnailSource?: CreativeThumbnailSource;
  thumbnailUrl?: string;
  imageUrl?: string;
  // Preferred feed image (analytics API, Aug 2026): when present it is the
  // most trustworthy creative image for the row and is tried FIRST in the
  // client fallback chain, ahead of imageUrl/thumbnailUrl.
  creativeImageUrl?: string;
  // Original playable clip URLs are distinct from image/thumbnail previews.
  // Analytics providers may use either spelling while the proxy normalizes the
  // value into the signed iteration handoff.
  creativeVideoUrl?: string;
  videoUrl?: string;
  // Server-attached My Ads attribution metadata. It is deliberately excluded
  // from feed preview fallback chains; no match is better than showing the
  // wrong creative.
  libraryImageUrl?: string;
  // Generated-ad id behind the conservative automatic library match. Manual
  // attribution remains authoritative and is not a shortcut source.
  matchedGeneratedAdId?: string;
  // Local creative selected by a person or discovered through the existing
  // conservative exact-name library linkage. It is display-only metadata for
  // the dashboard; metrics always remain owned by the external feed row.
  localAd?: CreativeAdMatchCandidate;
  attributionSource?: "manual" | "automatic";
  attributedAngle?: string;
  // Dashboard producer decision: a verified brand-local My Ads match (including
  // imported creatives), or conservative canonical stored-persona name evidence.
  // This does not describe the creative's creation/import origin. Never copied
  // from upstream match-like metadata or picker suggestions.
  personaOSAttributed?: boolean;
  // Opaque, server-signed handoff. It binds the external creative identity and
  // original media to this brand without trusting browser-supplied URLs.
  iterationSourceToken?: string;
  iterationMediaType?: "image" | "video";
  iterationUnavailableReason?: string;
}

export function creativeAdDisplayUrls(
  row: Pick<CreativeAdRow, "creativeImageUrl" | "thumbnailUrl" | "imageUrl">,
): string[] {
  return [...new Set(
    [row.creativeImageUrl, row.imageUrl, row.thumbnailUrl]
      .filter((value): value is string => typeof value === "string" && value.trim().length > 0),
  )];
}

export interface CreativeAdMatchCandidate {
  id: string;
  name: string;
  names?: string[];
  persona: string | null;
  product: string | null;
  angle: string | null;
  imageUrl: string | null;
  createdAt: string;
  projectId?: string | null;
  projectName?: string | null;
}

export type CreativeAdMatchConfidence = "high" | "medium" | "low";

export interface CreativeAdMatchSuggestion extends CreativeAdMatchCandidate {
  score: number;
  confidence: CreativeAdMatchConfidence;
}

export interface CreativeTrendPoint {
  date: string;
  producer: string;
  boosted: boolean;
  spend: number;
  impressions: number;
  clicks: number;
  atc: number;
  checkout?: number;
  purchases?: number;
  leads?: number;
}

// ---------------------------------------------------------------------------
// Metric formulas — ALWAYS from summed counts.
// ---------------------------------------------------------------------------

type Countable = Pick<
  CreativeAdRow,
  "spend" | "impressions" | "clicks" | "atc" | "checkout" | "purchases" | "revenue" | "leads"
>;

export function sumField<T>(rows: T[], field: keyof T): number {
  return rows.reduce((s, r) => s + (Number(r[field]) || 0), 0);
}

export interface CreativeAggregate {
  spend: number;
  impressions: number;
  clicks: number;
  atc: number;
  checkout: number;
  purchases: number;
  revenue: number;
  // Summed dynamic lead-event count (0 when the feed doesn't send it).
  leads: number;
  ctr: number | null; // %
  cpm: number | null; // $
  cpc: number | null; // $
  atcPerClick: number | null; // %
  costPerAtc: number | null; // $ — primary arbiter
  // "Complete checkout" — the brand's Meta setup fires this stage as a Lead
  // event; the feed exposes it as `checkout`. Spend ÷ checkouts.
  costPerCheckout: number | null; // $
  // Ground-truth efficiency: spend ÷ purchases, shown ALONGSIDE the checkout
  // metric (never replacing it).
  costPerPurchase: number | null; // $
  purchaseCvr: number | null; // %
  roas: number | null;
}

const ratio = (num: number, den: number): number | null => (den > 0 ? num / den : null);

// Aggregate a slice of ads into summed counts + derived ratios (null when the
// denominator is zero — the UI renders "—" instead of Infinity/NaN).
export function aggregateAds(ads: Countable[]): CreativeAggregate {
  const spend = sumField(ads, "spend");
  const impressions = sumField(ads, "impressions");
  const clicks = sumField(ads, "clicks");
  const atc = sumField(ads, "atc");
  const checkout = sumField(ads, "checkout");
  const purchases = sumField(ads, "purchases");
  const revenue = sumField(ads, "revenue");
  const leads = sumField(ads, "leads");
  return {
    spend, impressions, clicks, atc, checkout, purchases, revenue, leads,
    ctr: ratio(clicks, impressions) === null ? null : (clicks / impressions) * 100,
    cpm: ratio(spend, impressions) === null ? null : (spend / impressions) * 1000,
    cpc: ratio(spend, clicks),
    atcPerClick: ratio(atc, clicks) === null ? null : (atc / clicks) * 100,
    costPerAtc: ratio(spend, atc),
    costPerCheckout: ratio(spend, checkout),
    costPerPurchase: ratio(spend, purchases),
    purchaseCvr: ratio(purchases, clicks) === null ? null : (purchases / clicks) * 100,
    roas: ratio(revenue, spend),
  };
}

// Maturity gate: spend >= minSpend && impressions >= minImpressions
// (defaults $75 / 1500 = 20× min spend). Immature ads render translucent and
// are excluded from rankings/aggregates by default.
export const DEFAULT_MIN_SPEND = 75;
export const impressionsGateFor = (minSpend: number): number => Math.round(minSpend * 20);

export function isMatureAd(
  ad: Pick<CreativeAdRow, "spend" | "impressions">,
  minSpend: number = DEFAULT_MIN_SPEND,
  minImpressions: number = impressionsGateFor(minSpend),
): boolean {
  return ad.spend >= minSpend && ad.impressions >= minImpressions;
}

export type TopPerformerMetric = "leads" | "checkouts" | "purchases";

export function selectTopPerformerMetric(
  ads: readonly CreativeAdRow[],
  resolvedLeadEventName: string | null | undefined,
): TopPerformerMetric {
  if (resolvedLeadEventName) return "leads";
  // Evaluate the entire fetched window, not just importable static candidates.
  return ads.some((ad) => Number.isFinite(ad.purchases) && ad.purchases > 0)
    ? "purchases" : "checkouts";
}

export function topPerformerValue(ad: CreativeAdRow, metric: TopPerformerMetric): number {
  const value = metric === "checkouts" ? ad.checkout : ad[metric];
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : 0;
}

export function rankTopPerformers(
  ads: readonly CreativeAdRow[],
  { metric, minSpend = DEFAULT_MIN_SPEND }: { metric: TopPerformerMetric; minSpend?: number },
): CreativeAdRow[] {
  return ads.filter((ad) =>
    ad.format === "Static" && ad.iterationMediaType === "image" &&
    typeof ad.iterationSourceToken === "string" && !!ad.iterationSourceToken.trim() &&
    Number.isFinite(ad.spend) && Number.isFinite(ad.impressions) &&
    isMatureAd(ad, minSpend) && topPerformerValue(ad, metric) > 0,
  ).sort((a, b) => {
    const aValue = topPerformerValue(a, metric);
    const bValue = topPerformerValue(b, metric);
    return bValue - aValue || a.spend / aValue - b.spend / bValue || b.spend - a.spend;
  }).slice(0, 24);
}

// Persona breakdown "call" (§5.2.7). Order matters: Double down first, then
// Fund to validate, then Pause, else Iterate. costPerAtc null (no ATCs) with
// spend present is treated as expensive → Pause when spend is meaningful.
export type PersonaCall = "Double down" | "Fund to validate" | "Pause" | "Iterate";

export function personaCall(input: {
  costPerAtc: number | null;
  purchases: number;
  atcPerClick: number | null;
}): PersonaCall {
  const { costPerAtc, purchases, atcPerClick } = input;
  if (costPerAtc !== null && costPerAtc < 6 && purchases >= 10) return "Double down";
  if (purchases <= 1 && atcPerClick !== null && atcPerClick >= 40) return "Fund to validate";
  if (costPerAtc === null || costPerAtc > 15) return "Pause";
  return "Iterate";
}

// Merge trend rows across producer per date (producer comparison is dropped),
// optionally filtering boosted rows out first.
export function mergeTrendByDate(
  points: CreativeTrendPoint[],
  excludeBoosted: boolean,
): Array<{ date: string; spend: number; impressions: number; clicks: number; atc: number; checkout: number; purchases: number; leads: number }> {
  const byDate = new Map<string, { date: string; spend: number; impressions: number; clicks: number; atc: number; checkout: number; purchases: number; leads: number }>();
  for (const p of points) {
    if (excludeBoosted && p.boosted) continue;
    const row = byDate.get(p.date) || {
      date: p.date,
      spend: 0,
      impressions: 0,
      clicks: 0,
      atc: 0,
      checkout: 0,
      purchases: 0,
      leads: 0,
    };
    row.spend += p.spend || 0;
    row.impressions += p.impressions || 0;
    row.clicks += p.clicks || 0;
    row.atc += p.atc || 0;
    row.checkout += p.checkout || 0;
    row.purchases += p.purchases || 0;
    row.leads += p.leads || 0;
    byDate.set(p.date, row);
  }
  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
}

// ---------------------------------------------------------------------------
// Producer comparison (PersonaOS-made creative vs the brand's own in-house
// creative — both pools are the SAME brand's ads; the split is by who
// produced the creative). The feed's `producer` field is "Persona" for
// PersonaOS creative; anything else is the brand's own label. As a backstop,
// "personaos" in the ad name also marks PersonaOS-made creative.
// ---------------------------------------------------------------------------

export type ProducerGroup = "personaos" | "inhouse";

export const PRODUCER_LABELS: Record<ProducerGroup, string> = {
  personaos: "PersonaOS",
  inhouse: "In-house",
};

// UI label for the in-house group: prefer the brand's OWN producer label from
// the feed (e.g. "Naadam" — the most common non-PersonaOS producer value),
// then the brand name, then the generic fallback. The UI should never say
// "In-house" when the brand's real name is available.
export function deriveInhouseLabel(
  rows: Array<{ producer?: string; adName?: string }>,
  brandName?: string | null,
): string {
  const counts = new Map<string, number>();
  for (const r of rows) {
    if (classifyProducer(r) !== "inhouse") continue;
    const p = (r.producer || "").trim();
    if (!p) continue;
    counts.set(p, (counts.get(p) || 0) + 1);
  }
  let best: string | null = null;
  let bestN = 0;
  for (const [label, n] of counts) {
    if (n > bestN) { best = label; bestN = n; }
  }
  return best || (brandName || "").trim() || PRODUCER_LABELS.inhouse;
}

export function classifyProducer(row: { producer?: string; adName?: string; personaOSAttributed?: boolean }): ProducerGroup {
  // Verified local match or stored-persona evidence is independent of the
  // feed producer/name, including ads with no persona or literal PersonaOS tag.
  if (row.personaOSAttributed === true) return "personaos";
  const p = (row.producer || "").trim().toLowerCase();
  if (p === "persona" || p.includes("personaos")) return "personaos";
  if ((row.adName || "").toLowerCase().includes("personaos")) return "personaos";
  return "inhouse";
}

export function splitAdsByProducer<T extends { producer?: string; adName?: string }>(
  ads: T[],
): Record<ProducerGroup, T[]> {
  const out: Record<ProducerGroup, T[]> = { personaos: [], inhouse: [] };
  for (const a of ads) out[classifyProducer(a)].push(a);
  return out;
}

// Sum trend rows per date WITHIN each producer group (ratios are computed
// downstream from these per-date sums — never averaged across days/rows).
export function splitTrendByProducer(
  points: CreativeTrendPoint[],
  excludeBoosted: boolean,
): Record<ProducerGroup, ReturnType<typeof mergeTrendByDate>> {
  const groups = splitAdsByProducer(points);
  return {
    personaos: mergeTrendByDate(groups.personaos, excludeBoosted),
    inhouse: mergeTrendByDate(groups.inhouse, excludeBoosted),
  };
}

// Comparison metrics for the Both-mode delta scorecard. `betterWhen` encodes
// the direction in which PersonaOS "wins": higher CTR / ATC-per-click is
// better; lower CPM / cost-per-add-to-cart / cost-per-checkout (the
// checkout/Lead-stage event) / cost-per-purchase is better. Spend is
// context only (no better direction).
export const COMPARISON_METRICS: Array<{
  key: keyof CreativeAggregate;
  label: string;
  betterWhen: "higher" | "lower" | null;
}> = [
  { key: "spend", label: "Spend", betterWhen: null },
  { key: "ctr", label: "CTR", betterWhen: "higher" },
  { key: "cpm", label: "CPM", betterWhen: "lower" },
  { key: "atcPerClick", label: "ATC / click", betterWhen: "higher" },
  { key: "costPerAtc", label: "Cost / add to cart", betterWhen: "lower" },
  { key: "costPerCheckout", label: "Cost / checkout", betterWhen: "lower" },
  { key: "costPerPurchase", label: "Cost / purchase", betterWhen: "lower" },
];

export interface ProducerDelta {
  pct: number | null; // signed % difference, PersonaOS vs in-house
  better: boolean | null; // true when PersonaOS wins for this metric
}

// % delta of the PersonaOS value vs the in-house value, plus whether that
// makes PersonaOS "better" given the metric's direction. Null when either
// side is missing or the in-house base is zero (no meaningful ratio).
export function producerDelta(
  personaValue: number | null,
  inhouseValue: number | null,
  betterWhen: "higher" | "lower" | null,
): ProducerDelta {
  if (
    personaValue === null || inhouseValue === null ||
    !isFinite(personaValue) || !isFinite(inhouseValue) || inhouseValue === 0
  ) {
    return { pct: null, better: null };
  }
  const pct = ((personaValue - inhouseValue) / Math.abs(inhouseValue)) * 100;
  const better =
    betterWhen === null || pct === 0
      ? null
      : betterWhen === "higher"
        ? personaValue > inhouseValue
        : personaValue < inhouseValue;
  return { pct, better };
}

export function median(values: number[]): number | null {
  const v = values.filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
  if (v.length === 0) return null;
  const mid = Math.floor(v.length / 2);
  return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
}

// ---------------------------------------------------------------------------
// Persona drill-down (Task #2170): feed-ad → local-generation enrichment,
// iteration counting, angle splits, and the weighted cut-or-double-down
// verdict. All pure so the tsx critical suite can exercise them standalone.
//
// Linkage: generated_ads.default_name_snapshot carries the media-buyer naming
// convention ({Persona}_{Product}_{Angle}_Static_PersonaOS_{YYMMDD}{Letter})
// that the launched Meta ads (and therefore the analytics feed's adName) are
// named after. Matching is a normalized exact-name match against
// defaultNameSnapshot/customName — unmatched feed ads stay "Unknown angle"
// rather than being fuzzily (mis)attributed.
// ---------------------------------------------------------------------------

export const UNKNOWN_ANGLE = "Unknown angle";

// Narrative-angle details surfaced in the drill-down's angle breakdown
// (Task #2193). Served by /creative-performance/angles — read-only metadata
// from the brand's stored narrative angles.
export interface CreativeAngleDetail {
  name: string;
  family: string | null;
  subtype: string | null;
  summary: string | null;
  coreTension: string | null;
  beliefShift: string | null;
  promise: string | null;
  proofAnchors: string[];
}

// Normalized-name lookup of angle details. First writer wins for duplicate
// names so the result stays deterministic.
export function buildAngleDetailLookup(
  angles: CreativeAngleDetail[] | undefined,
): (angleName: string) => CreativeAngleDetail | null {
  const byName = new Map<string, CreativeAngleDetail>();
  for (const a of angles ?? []) {
    const key = normalizeAdName(a.name || "");
    if (key && !byName.has(key)) byName.set(key, a);
  }
  return (angleName: string) => byName.get(normalizeAdName(angleName || "")) ?? null;
}

// One local library ad the server exposes for enrichment (no metrics — just
// the name → angle/generation linkage).
export interface CreativeAdLink {
  adName: string | null; // defaultNameSnapshot (media-buyer convention)
  customName: string | null; // user rename — also launched under sometimes
  angleName: string | null; // narrative angle (or KC creative angle) name
  generationId: string; // ad_generations.id = one Ads Express run/iteration
}

export interface CreativeAdEnrichment {
  angle: string | null;
  generationId: string | null;
}

// Normalize an ad name for matching: case/punctuation-insensitive. The feed
// sometimes appends suffixes when duplicating ads, so exact-normalized match
// is tried first, then a prefix match on the longer of the two names.
export function normalizeAdName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

// Human-picker suggestions deliberately use simple, inspectable textual
// evidence. This is separate from automatic attribution: it may rank a
// plausible creative, but never mutates or attributes a row by itself.
export function scoreCreativeAdMatchSuggestions(
  ad: Pick<CreativeAdRow, "adName" | "persona" | "product">,
  candidates: CreativeAdMatchCandidate[],
): CreativeAdMatchSuggestion[] {
  const feedName = normalizeAdName(ad.adName || "");
  const parsed = parsePersonaOSAdName(ad.adName);
  const evidence = (value: string | null | undefined, feedValues: Array<string | null | undefined>) => {
    const key = normalizeAdName(value || "");
    if (!key) return false;
    return feedValues.some((feedValue) => key === normalizeAdName(feedValue || "")) || feedName.includes(key);
  };
  return candidates
    .map((candidate) => {
      const names = [...new Set([candidate.name, ...(candidate.names ?? [])])]
        .map(normalizeAdName)
        .filter(Boolean);
      const exactName = Boolean(feedName && names.includes(feedName));
      const persona = evidence(candidate.persona, [ad.persona, parsed?.persona]);
      const product = evidence(candidate.product, [ad.product, parsed?.product]);
      const angle = evidence(candidate.angle, [parsed?.angle, parsed?.sale]);
      const overlap = Number(persona) + Number(product) + Number(angle);
      // Exact canonical name is the strongest signal. Persona + product
      // outranks any other pair, then any one named field. A lack of evidence
      // is omitted entirely rather than looking like a weak recommendation.
      const score = exactName ? 100 : persona && product ? 70 : overlap >= 2 ? 60 : overlap === 1 ? 30 : 0;
      if (!score) return null;
      return {
        ...candidate,
        score,
        confidence: (score >= 100 ? "high" : score >= 60 ? "medium" : "low") as CreativeAdMatchConfidence,
      };
    })
    .filter((candidate): candidate is CreativeAdMatchSuggestion => candidate !== null)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      const aTime = Date.parse(a.createdAt) || 0;
      const bTime = Date.parse(b.createdAt) || 0;
      return bTime - aTime || a.id.localeCompare(b.id);
    });
}

// Attach a manual My Ads link as the final attribution layer. A human-confirmed
// library creative is authoritative: its image, persona, product, and angle
// should drive the dashboard, and the selected creative is PersonaOS-owned.
export function applyManualCreativeAdMatches<T extends {
  adId?: string; persona?: string | null; product?: string; libraryImageUrl?: string;
}>(
  rows: T[],
  matches: Map<string, CreativeAdMatchCandidate>,
): T[] {
  return rows.map((row) => {
    const match = row.adId ? matches.get(row.adId) : undefined;
    if (!match) return row;
    return {
      ...row,
      persona: match.persona || row.persona,
      product: match.product || row.product,
      libraryImageUrl: match.imageUrl || row.libraryImageUrl,
      localAd: match,
      attributionSource: "manual",
      attributedAngle: match.angle || undefined,
      personaOSAttributed: true,
    };
  });
}

const NO_ENRICHMENT: CreativeAdEnrichment = { angle: null, generationId: null };

// ---------------------------------------------------------------------------
// Agency naming convention (Task #2313). Gaffle's agency names launched ads
//   <batch>_s-agency_as-<format>[_m-<motif>]_<brand>[_<product>]_<persona>_<funnel>
// e.g. "260623G_s-agency_as-Static_m-offer_GaffleCoffeeShop_ClassicColdDrip_
//       Health & Fitness Coffee Drinker_BOF"
// The live feed carries quirks: doubled underscores, a batch code fused onto
// "s-agency" (missing underscore), brand+product fused into one segment
// ("GaffleCoffeeShopDubaiKoffee"), trailing " - Copy"/"(1)"/"_V2" suffixes,
// and spaces inside segments. The parser anchors on the "s-agency" marker,
// strips decoration segments (as-*/m-*/h-*/i-*), pops the trailing funnel
// (TOF/MOF/BOF), and returns the raw persona segment plus the brand/product
// zone segments. Extracted values are NEVER trusted directly — callers must
// validate them against the brand's stored persona/product names.
// ---------------------------------------------------------------------------

export interface ParsedAgencyAdName {
  /** Brand/product-zone segments (brand label, maybe a product, maybe fused). */
  productSegments: string[];
  /** Raw persona segment (may be "ICP" or freeform — validate before use). */
  persona: string | null;
  funnel: string | null; // TOF | MOF | BOF when present
}

export function parseAgencyAdName(name: string | null | undefined): ParsedAgencyAdName | null {
  if (!name) return null;
  // Strip trailing duplication decorations: " - Copy", " (1)" (repeatable).
  const cleaned = name.trim().replace(/(?:\s*[-–]\s*copy|\s*\(\d+\))+\s*$/i, "");
  const rawParts = cleaned.split("_").map((p) => p.trim());
  // Anchor: the "s-agency" source marker — sometimes fused onto the batch
  // code when the underscore was dropped ("260626Cs-agency").
  let anchor = -1;
  for (let i = 0; i < rawParts.length; i++) {
    const p = rawParts[i].toLowerCase();
    if (p === "s-agency" || (p.endsWith("s-agency") && /^\d/.test(p))) { anchor = i; break; }
  }
  if (anchor < 0) return null;
  let parts = rawParts.slice(anchor + 1).filter(Boolean); // drops doubled-underscore empties
  // Trailing version markers ("V2") sit after the funnel.
  while (parts.length && /^v\d+$/i.test(parts[parts.length - 1])) parts.pop();
  let funnel: string | null = null;
  if (parts.length && /^(tof|mof|bof)\b/i.test(parts[parts.length - 1])) {
    funnel = parts.pop()!.slice(0, 3).toUpperCase();
  }
  // Decoration segments: as-<format>, m-<motif>, h-<hook>, i-<influencer>.
  parts = parts.filter((p) => !/^(as|m|h|i)-/i.test(p));
  if (parts.length === 0) return null;
  // Persona is the segment right before the funnel; with a single remaining
  // segment there is only a brand label — no persona to extract.
  const persona = parts.length >= 2 ? parts.pop()! : null;
  return { productSegments: parts, persona, funnel };
}

// ---------------------------------------------------------------------------
// Conservative stored-name matcher (Task #2313). Matches a raw extracted
// segment against a brand's stored names (personas or products) using
// normalized comparison plus a variant layer:
//   - strip a trailing parenthetical ("Temecula Social Curator (25-35F)")
//   - strip a subtitle after " - " ("The Exclusive Discovery Seeker - Local…")
//   - strip a leading "The" ("The Iced Capooch" ↔ "IcedCapooch")
//   - close spelling variants: normalized edit distance ≤ 1 for long names
//     ("Healthy&FitnessCoffeeDrinker" → "Health & Fitness Coffee Drinker")
// A match must be unambiguous (exactly one canonical name) or it's rejected.
// ---------------------------------------------------------------------------

export interface StoredNameMatcher {
  match: (candidate: string | null | undefined) => string | null;
  /** Fused-segment match: stored name appearing as a SUFFIX of the candidate
   *  (brand+product fused, e.g. "GaffleCoffeeShopDubaiKoffee"). */
  matchSuffix: (candidate: string | null | undefined) => string | null;
  /** Flexible last resort: stored name embedded ANYWHERE in the candidate
   *  (normalized substring, min length guard, longest unique winner) — the
   *  same trick the angle substring scan uses. Catches freeform names and
   *  convention oddballs where positional parsing fails. */
  matchAnywhere: (candidate: string | null | undefined) => string | null;
  /** Conservative recovery for a substantial, whole-token leading portion of
   * a longer stored name in one free-form segment. Callers must opt into this
   * separately from the normal exact/embedded-name matching stages. */
  matchTruncatedPrefix: (candidate: string | null | undefined) => string | null;
}

// Bounded Levenshtein distance (returns max+1 when exceeded).
function editDistanceAtMost(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (cur[j] < rowMin) rowMin = cur[j];
    }
    if (rowMin > max) return max + 1;
    prev = cur;
  }
  return prev[b.length];
}

export function buildStoredNameMatcher(names: Array<string | null | undefined> | undefined): StoredNameMatcher {
  // normalized variant key → set of canonical names (ambiguity detection).
  const variants = new Map<string, Set<string>>();
  const prefixForms: Array<{ canonical: string; tokens: string[] }> = [];
  const addKey = (key: string, canonical: string) => {
    if (!key) return;
    let set = variants.get(key);
    if (!set) { set = new Set(); variants.set(key, set); }
    set.add(canonical);
  };
  for (const raw of names ?? []) {
    const canonical = (raw || "").trim();
    if (!canonical) continue;
    const forms = new Set<string>([canonical]);
    const noParen = canonical.replace(/\s*\([^)]*\)\s*$/, "").trim();
    if (noParen) forms.add(noParen);
    for (const f of Array.from(forms)) {
      const noSubtitle = f.split(/\s+[-–—]\s+/)[0].trim();
      if (noSubtitle) forms.add(noSubtitle);
    }
    for (const f of Array.from(forms)) {
      const noThe = f.replace(/^the\s+/i, "").trim();
      if (noThe) forms.add(noThe);
    }
    for (const f of forms) {
      addKey(normalizeAdName(f), canonical);
      const tokens = personaNameTokens(f);
      if (tokens.length) prefixForms.push({ canonical, tokens });
    }
  }
  const uniq = (set: Set<string> | undefined): string | null =>
    set && set.size === 1 ? Array.from(set)[0] : null;
  const match = (candidate: string | null | undefined): string | null => {
    const key = normalizeAdName((candidate || "").trim());
    if (!key || variants.size === 0) return null;
    const exact = uniq(variants.get(key)) ?? uniq(variants.get(key.replace(/^the/, "")));
    if (exact) return exact;
    // Close spelling variants — long keys only, tolerance scaled by length
    // (≤1 for 10+, ≤2 for 20+ normalized chars), unique winner required.
    if (key.length < 10) return null;
    const winners = new Set<string>();
    for (const [k, set] of variants) {
      if (k.length < 10) continue;
      const max = Math.min(key.length, k.length) >= 20 ? 2 : 1;
      if (editDistanceAtMost(key, k, max) <= max) for (const c of set) winners.add(c);
    }
    return winners.size === 1 ? Array.from(winners)[0] : null;
  };
  const matchSuffix = (candidate: string | null | undefined): string | null => {
    const key = normalizeAdName((candidate || "").trim());
    if (!key || variants.size === 0) return null;
    let bestLen = 0;
    const winners = new Set<string>();
    for (const [k, set] of variants) {
      // Guard against tiny stored names matching accidentally, and require
      // the candidate to carry a real prefix (i.e. actually fused).
      if (k.length < 6 || k.length >= key.length || !key.endsWith(k)) continue;
      if (k.length > bestLen) { bestLen = k.length; winners.clear(); }
      if (k.length === bestLen) for (const c of set) winners.add(c);
    }
    return winners.size === 1 ? Array.from(winners)[0] : null;
  };
  const matchAnywhere = (candidate: string | null | undefined): string | null => {
    const key = normalizeAdName((candidate || "").trim());
    if (!key || variants.size === 0) return null;
    let bestLen = 0;
    const winners = new Set<string>();
    for (const [k, set] of variants) {
      // Long-enough keys only — short stored names embedded by accident would
      // misattribute (e.g. a persona called "Local" inside "LocalOffer").
      if (k.length < 10 || !key.includes(k)) continue;
      if (k.length > bestLen) { bestLen = k.length; winners.clear(); }
      if (k.length === bestLen) for (const c of set) winners.add(c);
    }
    return winners.size === 1 ? Array.from(winners)[0] : null;
  };
  const matchTruncatedPrefix = (candidate: string | null | undefined): string | null => {
    // This is intentionally much stricter than substring matching. It only
    // accepts complete leading WORDS of a longer stored name, so a fragment
    // like "health guard" cannot stand in for "health guardian". Four tokens,
    // twenty normalized characters, and three non-filler words keep generic
    // fragments from becoming attribution evidence.
    const candidateTokens = personaNameTokens((candidate || "").trim());
    if (candidateTokens.length < 4 || prefixForms.length === 0) return null;
    const filler = new Set(["a", "an", "and", "for", "in", "of", "on", "or", "the", "to", "with"]);
    let bestChars = 0;
    let bestTokens = 0;
    let partialCollision = false;
    const winners = new Set<string>();
    for (const form of prefixForms) {
      if (form.tokens.length <= 4) continue; // must be a prefix of a LONGER name
      for (let start = 0; start <= candidateTokens.length - 4; start++) {
        let count = 0;
        while (
          count < form.tokens.length
          && start + count < candidateTokens.length
          && candidateTokens[start + count] === form.tokens[count]
        ) {
          count++;
        }
        if (count < 4 || count >= form.tokens.length) continue;
        const matched = form.tokens.slice(0, count);
        const chars = matched.join("").length;
        const meaningful = new Set(matched.filter((token) => !filler.has(token))).size;
        if (chars < 20 || meaningful < 3) continue;
        const nextCandidate = candidateTokens[start + count];
        const nextStored = form.tokens[count];
        // If the candidate continues with part of the next expected word (in
        // either direction), this is a partial-word collision, not a clean
        // truncation boundary. Unrelated following words are allowed because
        // media buyers commonly wrap the fragment in free-form labels.
        if (
          nextCandidate
          && nextStored
          && (nextStored.startsWith(nextCandidate) || nextCandidate.startsWith(nextStored))
        ) {
          partialCollision = true;
          continue;
        }
        if (chars > bestChars || (chars === bestChars && count > bestTokens)) {
          bestChars = chars;
          bestTokens = count;
          winners.clear();
        }
        if (chars === bestChars && count === bestTokens) winners.add(form.canonical);
      }
    }
    // A substantial prefix followed by part of its next expected word makes
    // the segment unsafe as truncated-name evidence for every canonical name.
    // Otherwise a sibling sharing the visible prefix could win merely because
    // its next word differs from the partial token.
    if (partialCollision) return null;
    return winners.size === 1 ? Array.from(winners)[0] : null;
  };
  return { match, matchSuffix, matchAnywhere, matchTruncatedPrefix };
}

// ---------------------------------------------------------------------------
// Canonical dashboard persona identity. Analytics feeds may abbreviate or
// reformat a stored persona name, but dashboard grouping must use one stable
// stored identity wherever the evidence identifies exactly one person. This is
// deliberately narrower than general fuzzy matching: exact normalized forms,
// a unique first name, and token-by-token abbreviations (e.g. "V. Smith") are
// accepted; every other label remains exactly as the feed supplied it.
// ---------------------------------------------------------------------------

export type CanonicalPersonaResolver = (label: string | null | undefined) => string | null;

function personaNameForms(name: string): string[] {
  const forms = new Set<string>();
  const add = (value: string) => {
    const trimmed = value.trim();
    if (trimmed) forms.add(trimmed);
  };
  add(name);
  add(name.replace(/\s*\([^)]*\)\s*$/, ""));
  for (const form of Array.from(forms)) add(form.split(/\s+[-–—]\s+/)[0]);
  for (const form of Array.from(forms)) add(form.replace(/^the\s+/i, ""));
  return Array.from(forms);
}

function personaNameTokens(name: string): string[] {
  return name.toLowerCase().match(/[a-z0-9]+/g) ?? [];
}

// Maps a feed persona label to exactly one stored persona name. First-name and
// initials are useful feed abbreviations only when they identify one stored
// persona; collisions deliberately return null rather than guessing.
export function buildCanonicalPersonaResolver(
  personaNames: Array<string | null | undefined> | undefined,
): CanonicalPersonaResolver {
  const exact = new Map<string, Set<string>>();
  const tokenForms: Array<{ canonical: string; tokens: string[] }> = [];
  const addExact = (key: string, canonical: string) => {
    if (!key) return;
    const matches = exact.get(key) ?? new Set<string>();
    matches.add(canonical);
    exact.set(key, matches);
  };
  for (const raw of personaNames ?? []) {
    const canonical = (raw || "").trim();
    if (!canonical) continue;
    for (const form of personaNameForms(canonical)) {
      addExact(normalizeAdName(form), canonical);
      const tokens = personaNameTokens(form);
      if (tokens.length > 0) tokenForms.push({ canonical, tokens });
    }
  }
  const one = (matches: Set<string> | undefined): string | null =>
    matches?.size === 1 ? Array.from(matches)[0] : null;
  const isAbbreviationOf = (candidate: string[], stored: string[]): boolean => {
    if (candidate.length === 0 || candidate.length > stored.length) return false;
    // A single token can only be a full, unique first name. A bare initial is
    // too weak an attribution signal even when today's stored list has one.
    if (candidate.length === 1) return candidate[0] === stored[0];
    for (let i = 0; i < candidate.length; i++) {
      const part = candidate[i];
      const target = stored[i];
      if (part === target) continue;
      if (part.length === 1 && target.startsWith(part)) continue;
      return false;
    }
    return true;
  };
  return (label) => {
    const raw = (label || "").trim();
    const key = normalizeAdName(raw);
    if (!key) return null;
    const direct = one(exact.get(key));
    if (direct) return direct;
    const candidate = personaNameTokens(raw);
    const matches = new Set<string>();
    for (const form of tokenForms) {
      if (isAbbreviationOf(candidate, form.tokens)) matches.add(form.canonical);
    }
    return matches.size === 1 ? Array.from(matches)[0] : null;
  };
}

// Materialize canonical stored-persona identity before any dashboard grouping,
// filtering, selection, or trend aggregation. Unmatched and ambiguous labels
// are retained verbatim, preventing a plausible-but-wrong attribution.
export function canonicalizeCreativePerformancePersonas<T extends { persona?: string | null }>(
  rows: T[],
  resolvePersona: CanonicalPersonaResolver,
): T[] {
  return rows.map((row) => {
    const raw = (row.persona || "").trim();
    const canonical = raw ? resolvePersona(raw) : null;
    return canonical && canonical !== row.persona ? { ...row, persona: canonical } : row;
  });
}

// Resolve a validated persona from a parsed agency name (null for ICP /
// freeform / ambiguous segments — no fuzzy guessing).
function agencyPersonaMatch(parsed: ParsedAgencyAdName | null, matcher: StoredNameMatcher): string | null {
  if (!parsed?.persona) return null;
  if (normalizeAdName(parsed.persona) === "icp") return null;
  return matcher.match(parsed.persona);
}

// Resolve a validated product from a parsed agency name: try each
// brand/product-zone segment (last first — the product follows the brand),
// then suffix matching for brand+product fused segments.
function agencyProductMatch(parsed: ParsedAgencyAdName | null, matcher: StoredNameMatcher): string | null {
  if (!parsed) return null;
  const segs = [...parsed.productSegments].reverse();
  for (const s of segs) {
    const hit = matcher.match(s);
    if (hit) return hit;
  }
  for (const s of segs) {
    const hit = matcher.matchSuffix(s);
    if (hit) return hit;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Persona derivation fallback (Task #2301). Some accounts' feeds return
// persona: null on EVERY row (verified live for Gaffle) even though the ad
// names follow the PersonaOS naming convention with the persona as the first
// segment. Derive the persona from the ad name — validated against the
// brand's stored persona names (and the persona segments of library ad
// names, the same source angle attribution trusts) — whenever the feed omits
// it. Feed-provided personas ALWAYS win; unparseable names or personas that
// don't match a stored name stay persona-less (no fuzzy guessing).
// ---------------------------------------------------------------------------

// Build a lookup from an ad name to a canonical persona name, or null when
// nothing matches. Known personas = the brand's stored persona names plus
// persona segments parsed from library ad names (both normalized; first
// writer wins so the canonical form is deterministic).
export function buildPersonaNameLookup(
  personaNames: Array<string | null | undefined> | undefined,
  links?: CreativeAdLink[],
): (adName: string | null | undefined) => string | null {
  const known = new Map<string, string>(); // normalized → canonical
  const add = (name: string | null | undefined) => {
    const trimmed = (name || "").trim();
    const key = normalizeAdName(trimmed);
    if (key && !known.has(key)) known.set(key, trimmed);
  };
  for (const n of personaNames ?? []) add(n);
  for (const l of links ?? []) {
    for (const raw of [l.adName, l.customName]) {
      const parsed = parsePersonaOSAdName(raw);
      if (parsed) add(parsed.persona);
    }
  }
  // Agency-convention fallback (Task #2313): validated matcher over the same
  // known persona names (stored names + library-ad persona segments).
  const matcher = buildStoredNameMatcher(Array.from(known.values()));
  return (adName: string | null | undefined) => {
    if (known.size === 0) return null;
    const raw = (adName || "").trim();
    if (!raw) return null;
    // Media buyers sometimes rewrap launched names with "|" separators — try
    // the raw name plus each segment, like angle attribution does.
    const candidates = [raw, ...(raw.includes("|") ? raw.split("|").map((s) => s.trim()).filter(Boolean) : [])];
    for (const c of candidates) {
      const parsed = parsePersonaOSAdName(c);
      if (!parsed) continue;
      const canonical = known.get(normalizeAdName(parsed.persona));
      if (canonical) return canonical;
    }
    // Agency convention second — only fires on names carrying the "s-agency"
    // marker, so PersonaOS/other-brand attribution stays byte-identical.
    for (const c of candidates) {
      const canonical = agencyPersonaMatch(parseAgencyAdName(c), matcher);
      if (canonical) return canonical;
    }
    // Flexible last resort: a stored persona name embedded anywhere in the
    // name (normalized substring, longest unique winner). Catches freeform
    // names and convention oddballs positional parsing misses.
    return matcher.matchAnywhere(raw);
  };
}

// ---------------------------------------------------------------------------
// Product derivation from agency ad names (Task #2313). The feed's product
// field for agency-run accounts is a generic placeholder ("Other/Mixed") —
// treat that (and blanks) as missing and fill from the agency-convention ad
// name when it validates against a stored product name. Real feed product
// labels always win.
// ---------------------------------------------------------------------------

const GENERIC_FEED_PRODUCTS = new Set(["", "othermixed", "other", "mixed", "unknown"]);

export function isGenericFeedProduct(product: string | null | undefined): boolean {
  return GENERIC_FEED_PRODUCTS.has(normalizeAdName((product || "").trim()));
}

// Ad name → canonical stored product name (agency convention only; null when
// nothing validates).
export function buildProductNameLookup(
  productNames: Array<string | null | undefined> | undefined,
): (adName: string | null | undefined) => string | null {
  const matcher = buildStoredNameMatcher(productNames);
  return (adName: string | null | undefined) => {
    const raw = (adName || "").trim();
    if (!raw) return null;
    const candidates = [raw, ...(raw.includes("|") ? raw.split("|").map((s) => s.trim()).filter(Boolean) : [])];
    for (const c of candidates) {
      const canonical = agencyProductMatch(parseAgencyAdName(c), matcher);
      if (canonical) return canonical;
    }
    // Flexible last resort — stored product name embedded anywhere in the
    // name. Same guardrails as the persona scan (min length, unique winner).
    return matcher.matchAnywhere(raw);
  };
}

// Fill generic/missing products on feed rows using the lookup. Real feed
// product values are never overwritten.
export function fillMissingProducts<T extends { product?: string | null; adName?: string | null }>(
  rows: T[],
  productForAdName: (adName: string | null | undefined) => string | null,
): T[] {
  return rows.map((r) => {
    if (!isGenericFeedProduct(r.product)) return r;
    const derived = productForAdName(r.adName);
    return derived ? { ...r, product: derived } : r;
  });
}

// Fill missing personas on feed rows using the lookup. Returns new row
// objects only where a persona was derived; feed values are never
// overwritten (empty/whitespace counts as missing).
export function fillMissingPersonas<T extends { persona?: string | null; adName?: string | null }>(
  rows: T[],
  personaForAdName: (adName: string | null | undefined) => string | null,
): T[] {
  return rows.map((r) => {
    if ((r.persona || "").trim()) return r;
    const derived = personaForAdName(r.adName);
    return derived ? { ...r, persona: derived } : r;
  });
}

// ---------------------------------------------------------------------------
// Verified PersonaOS attribution. A feed-provided persona can be useful for
// display, but it is not provenance: only an unambiguous match against a
// stored brand persona, recovered from the ad name, marks a renamed creative
// as PersonaOS. The same verified canonical persona then guards any recovered
// narrative angle, so a globally matching angle cannot be attached to the
// wrong persona after a buyer rewrites a name.
// ---------------------------------------------------------------------------

export interface CreativeAdAttribution extends CreativeAdEnrichment {
  /** Canonical stored brand persona, or null when the name is not verified. */
  persona: string | null;
  /** True only for a verified stored-persona name match. */
  personaOSAttributed: boolean;
}

export interface CreativeAttributionOptions {
  personaNames?: Array<string | null | undefined>;
  links?: CreativeAdLink[];
  knownAngleNames?: string[];
  knownProductNames?: Array<string | null | undefined>;
}

const NO_CREATIVE_ATTRIBUTION: CreativeAdAttribution = {
  persona: null,
  angle: null,
  generationId: null,
  personaOSAttributed: false,
};

// Unlike the legacy persona fallback, this resolver intentionally validates
// only against currently stored brand personas. Library-name-only identities
// are not sufficient provenance for producer attribution.
export function buildVerifiedPersonaNameLookup(
  personaNames: Array<string | null | undefined> | undefined,
): (adName: string | null | undefined) => string | null {
  const matcher = buildStoredNameMatcher(personaNames);
  return (adName: string | null | undefined) => {
    const raw = (adName || "").trim();
    if (!raw) return null;
    // Pipe wrappers are independent launch-name segments. Never scan the
    // separator-stripped raw wrapper: that can manufacture a stored name
    // across two unrelated segments. Aggregate every segment-level signal and
    // accept only one canonical winner.
    const candidates = raw.includes("|")
      ? raw.split("|").map((s) => s.trim()).filter(Boolean)
      : [raw];
    const winners = new Set<string>();
    for (const candidate of candidates) {
      // Preserve stronger convention, agency, and complete embedded-name
      // evidence. The truncated-prefix arm may only speak when this individual
      // pipe segment has none of those signals; evidence from another segment
      // still participates so conflicting wrappers fail closed.
      const strict = new Set<string>();
      const parsed = parsePersonaOSAdName(candidate);
      const canonical = parsed ? matcher.match(parsed.persona) : null;
      if (canonical) strict.add(canonical);
      const agencyCanonical = agencyPersonaMatch(parseAgencyAdName(candidate), matcher);
      if (agencyCanonical) strict.add(agencyCanonical);
      const embeddedCanonical = matcher.matchAnywhere(candidate);
      if (embeddedCanonical) strict.add(embeddedCanonical);
      for (const hit of strict) winners.add(hit);
      if (strict.size === 0) {
        const truncatedCanonical = matcher.matchTruncatedPrefix(candidate);
        if (truncatedCanonical) winners.add(truncatedCanonical);
      }
    }
    return winners.size === 1 ? Array.from(winners)[0] : null;
  };
}

// One shared resolver for Creative Performance. It retains the established
// exact/prefix linkage behavior, while requiring name evidence tied to the
// verified persona before a recovered (non-exact) angle can be surfaced.
export function buildCreativeAttributionLookup(
  options: CreativeAttributionOptions,
): (adName: string | null | undefined) => CreativeAdAttribution {
  const personaMatcher = buildStoredNameMatcher(options.personaNames);
  const personaForAdName = buildVerifiedPersonaNameLookup(options.personaNames);
  const enrichForAdName = buildAdEnrichmentLookup(
    options.links,
    options.knownAngleNames,
    {
      knownPersonaNames: options.personaNames,
      knownProductNames: options.knownProductNames,
    },
  );
  const knownAngles = new Map<string, string>();
  for (const angle of options.knownAngleNames ?? []) {
    const canonical = (angle || "").trim();
    const key = normalizeAdName(canonical);
    if (key && !knownAngles.has(key)) knownAngles.set(key, canonical);
  }
  // Local history says which canonical personas have previously used an
  // angle. This supports altered names only after the persona was verified.
  const personasForAngle = new Map<string, Set<string>>();
  const directByName = new Map<string, CreativeAdEnrichment>();
  for (const link of options.links ?? []) {
    const angle = (link.angleName || "").trim();
    if (!angle) continue;
    const canonicalAngle = knownAngles.get(normalizeAdName(angle)) ?? angle;
    const enrich = { angle: canonicalAngle, generationId: link.generationId || null };
    let linkedPersona: string | null = null;
    for (const name of [link.adName, link.customName]) {
      const canonicalPersona = personaForAdName(name);
      if (canonicalPersona) linkedPersona = canonicalPersona;
      const key = normalizeAdName(name || "");
      if (key && !directByName.has(key)) directByName.set(key, enrich);
    }
    if (linkedPersona) {
      const set = personasForAngle.get(normalizeAdName(canonicalAngle)) ?? new Set<string>();
      set.add(linkedPersona);
      personasForAngle.set(normalizeAdName(canonicalAngle), set);
    }
  }
  const directEvidence = (name: string): CreativeAdEnrichment | null => {
    const raw = name.trim();
    if (!raw) return null;
    const candidates = [raw, ...(raw.includes("|") ? raw.split("|").map((s) => s.trim()).filter(Boolean) : [])];
    let bestKey = "";
    let best: CreativeAdEnrichment | null = null;
    let ambiguous = false;
    for (const candidate of candidates) {
      const key = normalizeAdName(candidate);
      for (const [linkedName, enrich] of directByName) {
        if (!key || (!key.startsWith(linkedName) && !linkedName.startsWith(key))) continue;
        if (linkedName.length > bestKey.length) {
          bestKey = linkedName;
          best = enrich;
          ambiguous = false;
        } else if (linkedName.length === bestKey.length && best
          && (best.angle !== enrich.angle || best.generationId !== enrich.generationId)) {
          ambiguous = true;
        }
      }
    }
    return ambiguous ? null : best;
  };
  // Name evidence is independently sufficient when exactly one stored angle
  // is visibly embedded in the changed/wrapped name. This mirrors the existing
  // conservative longest-unique rule (including its short-name guard), while
  // keeping persona+product/history inference on the separate guarded path.
  const explicitAngleInName = (name: string): string | null => {
    const candidates = name.includes("|")
      ? name.split("|").map((s) => s.trim()).filter(Boolean)
      : [name];
    const winners = new Set<string>();
    for (const candidate of candidates) {
      const fullKey = normalizeAdName(candidate);
      let bestKey: string | null = null;
      let ambiguous = false;
      for (const key of knownAngles.keys()) {
        if (key.length < 8 || !fullKey.includes(key)) continue;
        if (bestKey === null || key.length > bestKey.length) {
          bestKey = key;
          ambiguous = false;
        } else if (key.length === bestKey.length && knownAngles.get(key) !== knownAngles.get(bestKey)) {
          ambiguous = true;
        }
      }
      if (!ambiguous && bestKey) winners.add(knownAngles.get(bestKey)!);
    }
    return winners.size === 1 ? Array.from(winners)[0] : null;
  };
  const parsedAngleSupportsPersona = (name: string, persona: string, angle: string): boolean => {
    const raw = name.trim();
    const candidates = [raw, ...(raw.includes("|") ? raw.split("|").map((s) => s.trim()).filter(Boolean) : [])];
    for (const candidate of candidates) {
      const parsed = parsePersonaOSAdName(candidate);
      if (!parsed || personaMatcher.match(parsed.persona) !== persona) continue;
      for (const part of [parsed.angle, parsed.sale]) {
        if (part && normalizeAdName(part) === normalizeAdName(angle)) return true;
      }
    }
    return false;
  };
  return (adName: string | null | undefined) => {
    const raw = (adName || "").trim();
    if (!raw) return NO_CREATIVE_ATTRIBUTION;
    const persona = personaForAdName(raw);
    const enrichment = enrichForAdName(raw);
    // No verified persona means preserve existing angle-link behavior, but do
    // not upgrade producer provenance.
    if (!persona) return { ...enrichment, persona: null, personaOSAttributed: false };
    if (!enrichment.angle) {
      return { persona, angle: null, generationId: null, personaOSAttributed: true };
    }
    const canonicalAngle = knownAngles.get(normalizeAdName(enrichment.angle)) ?? enrichment.angle;
    const direct = directEvidence(raw);
    const anglePersonas = personasForAngle.get(normalizeAdName(canonicalAngle));
    const supported =
      (Boolean(direct?.angle) && normalizeAdName(direct!.angle!) === normalizeAdName(canonicalAngle))
      || normalizeAdName(explicitAngleInName(raw) ?? "") === normalizeAdName(canonicalAngle)
      || parsedAngleSupportsPersona(raw, persona, canonicalAngle)
      || Boolean(anglePersonas?.has(persona));
    return supported
      ? { ...enrichment, angle: canonicalAngle, persona, personaOSAttributed: true }
      : { persona, angle: null, generationId: null, personaOSAttributed: true };
  };
}

// Materialize verified identity before producer filtering/grouping. A canonical
// match intentionally overrides a non-canonical feed persona label; unmatched
// feed values stay intact and remain in-house unless legacy producer evidence
// already classifies them as PersonaOS.
export function applyCreativeAttribution<
  T extends { adName?: string | null; persona?: string | null; personaOSAttributed?: boolean; attributionSource?: string },
>(
  rows: T[],
  attributionForAdName: (adName: string | null | undefined) => CreativeAdAttribution,
): T[] {
  return rows.map((row) => {
    // A human-confirmed My Ads link is explicit attribution and must not be
    // overwritten by the conservative automatic persona resolver.
    if (row.attributionSource === "manual") return row;
    const attribution = attributionForAdName(row.adName);
    if (!attribution.personaOSAttributed || !attribution.persona) return row;
    return {
      ...row,
      persona: attribution.persona,
      personaOSAttributed: true,
    };
  });
}

// Task #2193 — when no library row matches, fall back to parsing the
// PersonaOS naming convention and validating the extracted angle (and, since
// the sale segment is optional, the segment after it) against the brand's
// stored narrative-angle names. Returns the CANONICAL stored name so the
// detail lookup keys align. generationId stays null (no run linkage).
export function buildAdEnrichmentLookup(
  links: CreativeAdLink[] | undefined,
  knownAngleNames?: string[],
  // Task #2313 — stored persona/product names enable the agency-convention
  // stage: derive a validated persona+product pair from an agency-named feed
  // ad and resolve the angle ONLY through the same persona+product →
  // unique-angle rule the pp fallback uses. Omitted = stage disabled
  // (byte-identical output for existing callers).
  agencyOpts?: { knownPersonaNames?: Array<string | null | undefined>; knownProductNames?: Array<string | null | undefined> },
): (adName: string) => CreativeAdEnrichment {
  const knownAngles = new Map<string, string>(); // normalized → canonical
  for (const n of knownAngleNames ?? []) {
    const key = normalizeAdName(n || "");
    if (key && !knownAngles.has(key)) knownAngles.set(key, n.trim());
  }
  const parsedFallback = (adName: string): CreativeAdEnrichment | null => {
    if (knownAngles.size === 0) return null;
    const parsed = parsePersonaOSAdName(adName);
    if (!parsed) return null;
    // Angle segment first; if a sale segment exists it could actually be the
    // angle of a shifted legacy name, so try it second.
    for (const candidate of [parsed.angle, parsed.sale]) {
      if (!candidate) continue;
      const canonical = knownAngles.get(normalizeAdName(candidate));
      if (canonical) return { angle: canonical, generationId: null };
    }
    return null;
  };
  const byName = new Map<string, CreativeAdEnrichment>();
  // Structured persona+product index (Task #2193 follow-up): media buyers
  // often relaunch ads with the ANGLE segment replaced by a color/variant
  // (e.g. "Sophie - Ambitious Urbanite_Boatneck Sweater - Ivory"), so the
  // only recoverable linkage is the persona+product pair. Library names
  // follow the strict convention, so parse THEM and index by the normalized
  // contiguous "personaproduct" prefix.
  type PpEntry = { pp: string; personaLen: number; angles: Set<string>; gens: Set<string> };
  const ppIndex = new Map<string, PpEntry>();
  for (const l of links ?? []) {
    const enrich: CreativeAdEnrichment = {
      angle: (l.angleName || "").trim() || null,
      generationId: l.generationId || null,
    };
    for (const raw of [l.adName, l.customName]) {
      const key = raw ? normalizeAdName(raw) : "";
      // First writer wins so ambiguous duplicate names stay deterministic.
      if (key && !byName.has(key)) byName.set(key, enrich);
    }
    if (enrich.angle) {
      const parsed = parsePersonaOSAdName(l.adName);
      if (parsed) {
        const personaKey = normalizeAdName(parsed.persona);
        const productKey = normalizeAdName(parsed.product);
        if (personaKey && productKey) {
          const pp = personaKey + productKey;
          const entry = ppIndex.get(pp) || { pp, personaLen: personaKey.length, angles: new Set<string>(), gens: new Set<string>() };
          entry.angles.add(enrich.angle);
          if (enrich.generationId) entry.gens.add(enrich.generationId);
          ppIndex.set(pp, entry);
        }
      }
    }
  }
  const ppEntries = Array.from(ppIndex.values());
  // Persona+product fallback: find the entry whose "personaproduct" prefix
  // is most fully contained in the feed name (allowing a short truncation —
  // launched names cut off mid-product). Attribute only when the winning
  // entry maps to exactly ONE angle; otherwise the angle is unrecoverable.
  const ppFallback = (fullKey: string): CreativeAdEnrichment | null => {
    let best: PpEntry | null = null;
    let bestScore = 0;
    let tie = false;
    for (const e of ppEntries) {
      // Longest prefix of e.pp appearing as a substring of the feed name.
      let lo = 0;
      let hi = e.pp.length;
      while (lo < hi) {
        const m = Math.floor((lo + hi + 1) / 2);
        if (fullKey.includes(e.pp.slice(0, m))) lo = m; else hi = m - 1;
      }
      const score = lo;
      // Must cover the full persona plus (almost) the full product — allow
      // up to 12 normalized chars of product truncation.
      const required = e.personaLen + Math.max(4, e.pp.length - e.personaLen - 12);
      if (score < 16 || score < required) continue;
      if (score > bestScore) { best = e; bestScore = score; tie = false; }
      else if (score === bestScore && best && e !== best) tie = true;
    }
    if (!best || tie || best.angles.size !== 1) return null;
    return {
      angle: Array.from(best.angles)[0],
      generationId: best.gens.size === 1 ? Array.from(best.gens)[0] : null,
    };
  };
  // Agency-convention stage (Task #2313): parse the agency name, validate the
  // persona + product against stored names, and resolve the angle solely via
  // the persona+product → unique-angle index built from library links above.
  // No fuzzy angle picking: ambiguous pairs / ICP / freeform names stay null.
  const personaMatcher = buildStoredNameMatcher(agencyOpts?.knownPersonaNames);
  const productMatcher = buildStoredNameMatcher(agencyOpts?.knownProductNames);
  const uniqueAngleForPair = (persona: string | null, product: string | null): CreativeAdEnrichment | null => {
    if (!persona || !product) return null;
    const entry = ppIndex.get(normalizeAdName(persona) + normalizeAdName(product));
    if (!entry || entry.angles.size !== 1) return null;
    return {
      angle: Array.from(entry.angles)[0],
      generationId: entry.gens.size === 1 ? Array.from(entry.gens)[0] : null,
    };
  };
  const agencyFallback = (candidates: string[]): CreativeAdEnrichment | null => {
    if (!agencyOpts || ppIndex.size === 0) return null;
    for (const c of candidates) {
      const parsed = parseAgencyAdName(c);
      if (!parsed) continue;
      const persona = agencyPersonaMatch(parsed, personaMatcher);
      const product = agencyProductMatch(parsed, productMatcher);
      const hit = uniqueAngleForPair(persona, product);
      if (hit) return hit;
    }
    // Flexible last resort: stored persona AND product names embedded
    // anywhere in the name (longest unique winners) → the same
    // persona+product → unique-angle rule. Still no fuzzy angle picking.
    for (const c of candidates) {
      const hit = uniqueAngleForPair(personaMatcher.matchAnywhere(c), productMatcher.matchAnywhere(c));
      if (hit) return hit;
    }
    return null;
  };
  const keys = Array.from(byName.keys());
  // Exact + prefix match for ONE candidate name (normalized). Prefix
  // fallback: the feed name is the library name plus a launch suffix (e.g.
  // "_1", " copy") or a truncated cut of it, or vice versa. Prefer the
  // LONGEST (most specific) matching key; bail to unknown only when two
  // different enrichments tie.
  const resolveKey = (key: string): CreativeAdEnrichment | null => {
    if (!key) return null;
    const exact = byName.get(key);
    if (exact) return exact;
    let bestKey: string | null = null;
    let best: CreativeAdEnrichment | null = null;
    let tie = false;
    for (const k of keys) {
      if (!key.startsWith(k) && !k.startsWith(key)) continue;
      if (bestKey === null || k.length > bestKey.length) {
        bestKey = k; best = byName.get(k)!; tie = false;
      } else if (k.length === bestKey.length && byName.get(k) !== best) {
        tie = true;
      }
    }
    return tie ? null : best;
  };
  // Known-angle substring scan: media buyers often rewrap launched names
  // (e.g. "Image | 080726 | {original name} | PersonaOS"), so as a last
  // resort look for a stored angle name embedded anywhere in the normalized
  // feed name. Longest match wins; a tie between different angles bails.
  const angleKeys = Array.from(knownAngles.keys());
  const angleSubstringFallback = (fullKey: string): CreativeAdEnrichment | null => {
    let bestKey: string | null = null;
    let tie = false;
    for (const k of angleKeys) {
      // Guard against tiny angle names matching accidentally.
      if (k.length < 8 || !fullKey.includes(k)) continue;
      if (bestKey === null || k.length > bestKey.length) { bestKey = k; tie = false; }
      else if (k.length === bestKey.length && knownAngles.get(k) !== knownAngles.get(bestKey)) tie = true;
    }
    if (tie || !bestKey) return null;
    return { angle: knownAngles.get(bestKey)!, generationId: null };
  };
  const uniqueFallback = (hits: Array<CreativeAdEnrichment | null>): CreativeAdEnrichment | null => {
    const matches = hits.filter((hit): hit is CreativeAdEnrichment => Boolean(hit?.angle));
    if (matches.length === 0) return null;
    const angles = new Map<string, string>();
    const generations = new Set<string>();
    for (const match of matches) {
      const key = normalizeAdName(match.angle || "");
      if (key && !angles.has(key)) angles.set(key, match.angle!);
      if (match.generationId) generations.add(match.generationId);
    }
    if (angles.size !== 1) return null;
    return {
      angle: Array.from(angles.values())[0],
      generationId: generations.size === 1 ? Array.from(generations)[0] : null,
    };
  };
  return (adName: string) => {
    // Candidate name forms: the raw feed name plus each "|"-separated
    // segment of it (media-buyer wrappers put the original launched name in
    // one of the segments). Try library matching first for every candidate,
    // then the naming-convention parser, then the angle substring scan.
    const raw = (adName || "").trim();
    if (!raw) return NO_ENRICHMENT;
    const segments = raw.includes("|")
      ? raw.split("|").map((s) => s.trim()).filter(Boolean)
      : [raw];
    // Preserve exact/prefix local-link behavior against both the complete feed
    // name and wrapped segments. All inferred name evidence below is scoped to
    // individual segments so separators cannot manufacture a persona/product
    // pair or narrative angle.
    const exactCandidates = raw.includes("|") ? [raw, ...segments] : segments;
    for (const c of exactCandidates) {
      const hit = resolveKey(normalizeAdName(c));
      if (hit) return hit;
    }
    const parsed = uniqueFallback(segments.map((segment) => parsedFallback(segment)));
    if (parsed) return parsed;
    const angle = uniqueFallback(segments.map((segment) => angleSubstringFallback(normalizeAdName(segment))));
    if (angle) return angle;
    const pair = uniqueFallback(segments.map((segment) => ppFallback(normalizeAdName(segment))));
    if (pair) return pair;
    return uniqueFallback(segments.map((segment) => agencyFallback([segment]))) ?? NO_ENRICHMENT;
  };
}

// Distinct iterations (batches / generation runs) behind a persona's ads.
// Feed `batch` labels count first; matched generation ids fill in for ads the
// feed didn't batch-label. A persona with ads but no batch/generation info
// still counts as 1 iteration (it was launched at least once).
export function countPersonaIterations(
  ads: Array<Pick<CreativeAdRow, "adName" | "batch">>,
  enrichFor: (adName: string) => CreativeAdEnrichment,
): number {
  const batches = new Set<string>();
  const gens = new Set<string>();
  let unattributed = 0;
  for (const a of ads) {
    const batch = (a.batch || "").trim();
    if (batch) { batches.add(batch.toLowerCase()); continue; }
    const gen = enrichFor(a.adName).generationId;
    if (gen) gens.add(gen);
    else unattributed += 1;
  }
  const n = batches.size + gens.size;
  if (n > 0) return n;
  return unattributed > 0 ? 1 : 0;
}

// Per-angle split of a persona's ads. Aggregates/calls come from MATURE ads
// only (summed counts, same rule as everywhere); immature ads are listed but
// never drive the call. Unknown-angle ads are grouped, not hidden.
export interface PersonaAngleSplit {
  angle: string; // display label; UNKNOWN_ANGLE for unmatched ads
  known: boolean;
  ads: CreativeAdRow[];
  matureAds: CreativeAdRow[];
  agg: CreativeAggregate; // over mature ads
  call: PersonaCall | null; // null when no mature ads in the angle
}

export function buildAngleSplits(
  ads: CreativeAdRow[],
  enrichFor: (adName: string) => CreativeAdEnrichment,
  minSpend: number = DEFAULT_MIN_SPEND,
  minImpressions: number = impressionsGateFor(minSpend),
): PersonaAngleSplit[] {
  const map = new Map<string, CreativeAdRow[]>();
  for (const a of ads) {
    const angle = a.attributedAngle || enrichFor(a.adName).angle || UNKNOWN_ANGLE;
    const list = map.get(angle) || [];
    list.push(a);
    map.set(angle, list);
  }
  return Array.from(map.entries())
    .map(([angle, list]) => {
      const matureAds = list.filter((a) => isMatureAd(a, minSpend, minImpressions));
      const agg = aggregateAds(matureAds);
      return {
        angle,
        known: angle !== UNKNOWN_ANGLE,
        ads: list,
        matureAds,
        agg,
        call: matureAds.length > 0
          ? personaCall({ costPerAtc: agg.costPerAtc, purchases: agg.purchases, atcPerClick: agg.atcPerClick })
          : null,
      };
    })
    // Cheapest Cost/ATC first; Unknown angle always last.
    .sort((a, b) => {
      if (a.known !== b.known) return a.known ? -1 : 1;
      return (a.agg.costPerAtc ?? Infinity) - (b.agg.costPerAtc ?? Infinity);
    });
}

// Weighted verdict. Extends (never contradicts) personaCall: performing
// personas keep their Double down / Fund to validate call; underperforming
// ones get "Try new angle" when only one angle was tried, otherwise they stay
// on their base Pause/Iterate call. Immature-only personas get "Needs more
// data". The iteration-count "Retire" escalation was removed by user request
// (2026-08-10) — the "Retire" action stays in the type but is never emitted.

export type PersonaVerdictAction = PersonaCall | "Retire" | "Try new angle" | "Needs more data";

export interface PersonaVerdict {
  action: PersonaVerdictAction;
  baseCall: PersonaCall | null; // personaCall over the mature aggregate
  rationale: string;
  iterations: number;
  matureCount: number;
  totalCount: number;
  knownAngleCount: number; // distinct known angles with >=1 mature ad
  angleSplits: PersonaAngleSplit[];
}

export function personaVerdict(
  ads: CreativeAdRow[],
  enrichFor: (adName: string) => CreativeAdEnrichment,
  minSpend: number = DEFAULT_MIN_SPEND,
  minImpressions: number = impressionsGateFor(minSpend),
): PersonaVerdict {
  const angleSplits = buildAngleSplits(ads, enrichFor, minSpend, minImpressions);
  const matureAds = ads.filter((a) => isMatureAd(a, minSpend, minImpressions));
  const iterations = countPersonaIterations(ads, enrichFor);
  const totalCount = ads.length;
  const matureCount = matureAds.length;
  const maturityNote = `${matureCount} of ${totalCount} ads mature (over $${minSpend} / ${minImpressions.toLocaleString()} impressions)`;

  const knownMature = angleSplits.filter((s) => s.known && s.matureAds.length > 0);
  const knownAngleCount = knownMature.length;

  const base = (action: PersonaVerdictAction, baseCall: PersonaCall | null, rationale: string): PersonaVerdict => ({
    action, baseCall, rationale, iterations, matureCount, totalCount, knownAngleCount, angleSplits,
  });

  if (matureCount === 0) {
    return base(
      "Needs more data",
      null,
      `No mature ads yet — ${maturityNote}. Let spend accrue before making a call.`,
    );
  }

  const agg = aggregateAds(matureAds);
  const call = personaCall({ costPerAtc: agg.costPerAtc, purchases: agg.purchases, atcPerClick: agg.atcPerClick });

  if (call === "Double down") {
    return base("Double down", call,
      `Performing → double down: cost per add to cart ${agg.costPerAtc === null ? "—" : `$${agg.costPerAtc.toFixed(2)}`} with ${agg.purchases} purchases across mature ads. ${maturityNote}.`);
  }
  if (call === "Fund to validate") {
    return base("Fund to validate", call,
      `Strong ATC-per-click but almost no purchases yet — fund to validate before judging. ${maturityNote}.`);
  }

  // Underperforming (Pause / Iterate) — attribute angle vs persona.
  const workingAngles = knownMature.filter((s) => s.call === "Double down");
  if (workingAngles.length > 0) {
    const worst = [...knownMature].reverse().find((s) => s.call !== "Double down");
    return base("Iterate", call,
      `Angle problem, not a persona problem: "${workingAngles[0].angle}" performs${worst ? ` while "${worst.angle}" lags` : ""} — cut the weak angle and scale the working one. ${maturityNote}.`);
  }
  if (knownAngleCount <= 1) {
    return base("Try new angle", call,
      `Underperforming, but only ${knownAngleCount === 1 ? `one angle ("${knownMature[0].angle}")` : "one angle"} has mature data → try a new narrative angle before cutting. ${maturityNote}.`);
  }
  return base(call, call,
    `${call === "Pause" ? "Costs are running high across angles" : "Middling results across angles"} after ${iterations} iteration${iterations === 1 ? "" : "s"} — ${call === "Pause" ? "pause and rework the creative" : "keep iterating"}. ${maturityNote}.`);
}

// ---------------------------------------------------------------------------
// Per-brand dashboard config, stored at brands.settings.creativePerformance.
// Only the secret *name* (keySecretRef) is stored — never the raw key. The
// ref must use the CREATIVE_API_KEY_ prefix so the proxy can never be pointed
// at an arbitrary server env var.
// ---------------------------------------------------------------------------

export const CREATIVE_KEY_SECRET_PREFIX = "CREATIVE_API_KEY_";

// Shared admin key (can see every account; must pass account=<id> upstream).
// Brands configured with an accountId use this key; brands configured with a
// keySecretRef use their own brand-scoped key. keySecretRef wins if both set.
export const CREATIVE_ADMIN_KEY_REF = "CREATIVE_API_KEY_ADMIN";

// Account row from GET /creative/accounts (admin key only).
export interface CreativeAccount {
  accountId: string;
  accountName: string;
  firstDate?: string;
  lastDate?: string;
}

// Lead-gen scorecard mode (Task 2363): which upstream ad-row count field is
// treated as "leads". The lead event usually rides the feed's `checkout`
// column (lead-event-rides-checkout precedent), so that's the default; the
// other summed count fields are selectable per brand in Admin.
export const LEAD_SOURCE_FIELDS = ["leads", "checkout", "purchases", "atc", "clicks"] as const;
export type LeadSourceField = (typeof LEAD_SOURCE_FIELDS)[number];
export const DEFAULT_LEAD_SOURCE_FIELD: LeadSourceField = "checkout";
export const isValidLeadSourceField = (f: unknown): f is LeadSourceField =>
  typeof f === "string" && (LEAD_SOURCE_FIELDS as readonly string[]).includes(f);

// The brand's Meta lead event, passed to the analytics feed as a `leadEvent`
// query param so the feed counts it dynamically per request — no per-account
// hardcoding upstream. Meta action types are dot/underscore identifiers
// (e.g. `complete_registration`, `lead`,
// `offsite_conversion.custom.<conversion_id>`).
export const isValidLeadEventName = (v: unknown): v is string =>
  typeof v === "string" && /^[A-Za-z0-9._-]{1,200}$/.test(v);

// Stable account-funnel step ids are persisted in brand settings. Keep the
// accepted charset deliberately tighter than a query string and broad enough
// for both standard and mapped conversion identifiers.
export type CreativeFunnelStepKey = string;
export const isValidCreativeFunnelStepKey = (v: unknown): v is CreativeFunnelStepKey =>
  typeof v === "string" && /^[a-z][a-z0-9_:-]{0,63}$/.test(v);

// Kaya's external custom-conversions feed is the only conversion source
// currently enabled in Creative Performance. These ids are supplied by Kaya's
// analytics service; pixel_reservation intentionally does not participate in
// the reservation funnel because it is a separate measurement.
export const KAYA_CUSTOM_CONVERSIONS_ACCOUNT_ID = "2340132859710212";
export const KAYA_PRIMARY_CONVERSION_STEP = "ot_reservation";
export const KAYA_RESERVATION_INTENT_STEP = "reservation_intent";

export type CreativeFunnelStepSource = "mapping" | "standard";

export interface CreativeFunnelMeaning {
  key: CreativeFunnelStepKey;
  label: string;
  source: CreativeFunnelStepSource;
  known: boolean;
}

export interface CreativeFunnelStep extends CreativeFunnelMeaning {
  // known:true/count:0 is a tracked zero. known:false/count:null means the
  // account does not expose the step. Those states must never be collapsed.
  count: number | null;
  costPer: number | null;
}

export interface CreativeFunnelTotals {
  spend: number;
  impressions: number;
  steps: Record<CreativeFunnelStepKey, CreativeFunnelStep>;
}

export interface CreativeFunnelGateStats {
  matchedAds: number;
  eligibleAds: number;
  lowSpendAds: number;
}

export interface CreativeFunnelLowDataTotals {
  ads: number;
  spend: number | null;
  impressions: number | null;
}

export interface CreativeFunnelPersonaGroup {
  key: string;
  label: string;
  totals: CreativeFunnelTotals;
}

// Normalized response returned by our brand-scoped proxy. The upstream account
// id is deliberately omitted; callers already know the brand and must never be
// able to choose another account from the browser.
export interface CreativeFunnelResponse {
  // Set only for the Kaya custom-conversions adapter. The feed does not
  // provide ad eligibility/gate/persona dimensions, so the UI must not render
  // synthetic ad-level context for this source.
  conversionSource?: "kaya_custom_conversions";
  stepOrder: CreativeFunnelStepKey[];
  meanings: Record<CreativeFunnelStepKey, CreativeFunnelMeaning>;
  overall: CreativeFunnelTotals;
  stats: CreativeFunnelGateStats;
  lowData: CreativeFunnelLowDataTotals;
  groups: CreativeFunnelPersonaGroup[];
}

export function selectedCreativeFunnelStep(
  data: CreativeFunnelResponse | null | undefined,
  stepKey: CreativeFunnelStepKey | null | undefined,
  groupKey?: string | null,
): CreativeFunnelStep | null {
  if (!data || !stepKey) return null;
  const totals = groupKey
    ? data.groups.find((group) => group.key === groupKey)?.totals
    : data.overall;
  return totals?.steps[stepKey] ?? null;
}

// Lead-gen aggregate: Ad Spend / Leads / CPL from SUMMED counts over the same
// mature-ads slice the e-commerce scorecard uses. CPL is spend ÷ leads, null
// when leads is zero (UI renders "—", never Infinity).
export interface LeadGenAggregate {
  spend: number;
  leads: number;
  cpl: number | null;
  cpc: number | null;
  clickToLead: number | null;
}

// Current-period movement against a prior equivalent window. A zero or missing
// prior value has no meaningful percentage baseline, so callers render a quiet
// unavailable state rather than an invented percentage.
export function periodDelta(
  current: number | null,
  previous: number | null,
  betterWhen: "higher" | "lower" | null = null,
): ProducerDelta {
  return producerDelta(current, previous, betterWhen);
}

export function leadGenAggregate(
  agg: Pick<CreativeAggregate, "spend" | "leads" | "checkout" | "purchases" | "atc" | "clicks">,
  leadSourceField: LeadSourceField,
): LeadGenAggregate {
  const leads = Number(agg[leadSourceField]) || 0;
  return {
    spend: agg.spend,
    leads,
    cpl: leads > 0 ? agg.spend / leads : null,
    cpc: agg.clicks > 0 ? agg.spend / agg.clicks : null,
    clickToLead: agg.clicks > 0 ? (leads / agg.clicks) * 100 : null,
  };
}

export interface CreativePerformanceConfig {
  enabled: boolean;
  keySecretRef: string | null;
  accountId: string | null;
  defaultTier: CreativeTier;
  // Persona-first view mode: every brand defaults to the Persona-only
  // dashboard. Only brands with this flag ON additionally get the
  // producer/brand comparison view — and even then, comparison stops after
  // the top-level scorecard (never in the quadrant/funnel/trend sections).
  comparisonEnabled: boolean;
  // Date-period comparison is independently gated from producer comparison.
  // It unlocks only the lead-scorecard's previous-period option.
  dateComparisonEnabled: boolean;
  // Lead-gen scorecard mode (Task 2363): when ON the top scorecard renders
  // Ad Spend / Leads / CPL instead of the 7-tile e-commerce verdict. OFF
  // (the default) keeps the existing scorecard byte-identical.
  leadGenEnabled: boolean;
  leadSourceField: LeadSourceField;
  // Meta lead event for the dynamic upstream count; null = not configured
  // (the scorecard then falls back to leadSourceField).
  leadEventName: string | null;
  // Opt-in account-funnel conversion. Null preserves the legacy dashboard
  // exactly; a stable key makes the account-specific funnel authoritative for
  // the lead count, cost-per metric, funnel labels, and persona groups.
  primaryConversionStep: CreativeFunnelStepKey | null;
}

export function isValidCreativeKeyRef(ref: unknown): ref is string {
  return (
    typeof ref === "string" &&
    new RegExp(`^${CREATIVE_KEY_SECRET_PREFIX}[A-Z0-9_]+$`).test(ref)
  );
}

// Parse the raw brands.settings jsonb into a config. Returns a disabled
// default when absent/malformed. Invalid key refs are dropped (treated as not
// configured) rather than trusted.
export function parseCreativePerformanceSettings(
  settings: unknown,
): CreativePerformanceConfig {
  const disabled: CreativePerformanceConfig = {
    enabled: false, keySecretRef: null, accountId: null, defaultTier: "Cold", comparisonEnabled: false, dateComparisonEnabled: false,
    leadGenEnabled: false, leadSourceField: DEFAULT_LEAD_SOURCE_FIELD, leadEventName: null,
    primaryConversionStep: null,
  };
  if (!settings || typeof settings !== "object") return disabled;
  const raw = (settings as Record<string, unknown>).creativePerformance;
  if (!raw || typeof raw !== "object") return disabled;
  const cfg = raw as Record<string, unknown>;
  const keySecretRef = isValidCreativeKeyRef(cfg.keySecretRef) ? cfg.keySecretRef : null;
  const accountId = isValidCreativeAccountId(cfg.accountId) ? cfg.accountId : null;
  const defaultTier = CREATIVE_TIERS.includes(cfg.defaultTier as CreativeTier)
    ? (cfg.defaultTier as CreativeTier)
    : "Cold";
  // Default OFF: Persona-only is the baseline for all brands; comparison is
  // an explicit per-brand opt-in (absent/malformed => false). Same rule for
  // lead-gen mode; an invalid lead source drops to the checkout default.
  return {
    enabled: cfg.enabled === true, keySecretRef, accountId, defaultTier,
    comparisonEnabled: cfg.comparisonEnabled === true,
    dateComparisonEnabled: cfg.dateComparisonEnabled === true,
    leadGenEnabled: cfg.leadGenEnabled === true,
    leadSourceField: isValidLeadSourceField(cfg.leadSourceField) ? cfg.leadSourceField : DEFAULT_LEAD_SOURCE_FIELD,
    leadEventName: isValidLeadEventName(cfg.leadEventName) ? cfg.leadEventName : null,
    primaryConversionStep: isValidCreativeFunnelStepKey(cfg.primaryConversionStep) ? cfg.primaryConversionStep : null,
  };
}

// Build the next brands.settings blob for a creative-performance config save.
// One merge covers BOTH the analytics account mapping and the optional Ad
// Launcher flag: the admin dialog used to write them in two requests (config
// PUT + whole-settings brand PATCH built from a stale client copy), and the
// second write clobbered the account mapping the first had just written —
// detection then 409'd with "No connected ad account". Merging here, from the
// server's CURRENT settings, makes the save a single lost-update-free write.
export function mergeCreativePerformanceSettings(
  prevSettings: unknown,
  cfg: { enabled: boolean; keySecretRef: string | null; accountId: string | null; defaultTier: CreativeTier; comparisonEnabled?: boolean; dateComparisonEnabled?: boolean; leadGenEnabled?: boolean; leadSourceField?: LeadSourceField; leadEventName?: string | null; primaryConversionStep?: CreativeFunnelStepKey | null },
  adLauncher?: boolean,
): Record<string, unknown> {
  const prev = (prevSettings && typeof prevSettings === "object" ? prevSettings : {}) as Record<string, unknown>;
  const prevCreative = (
    prev.creativePerformance && typeof prev.creativePerformance === "object"
      ? prev.creativePerformance
      : {}
  ) as Record<string, unknown>;
  const creativePerformance: Record<string, unknown> = { ...cfg };
  // Internal rollout state is not editable client config, but it must survive
  // every Analytics save. Otherwise a later explicit opt-out would drop the
  // marker and a retried one-time seed could silently re-enable a default.
  if (prevCreative.leadGenStandardizationSeedVersion === "lead-gen-standardization-v1") {
    creativePerformance.leadGenStandardizationSeedVersion = "lead-gen-standardization-v1";
  }
  if (prevCreative.kayaCustomConversionsSeedVersion === "ot-reservation-v1") {
    creativePerformance.kayaCustomConversionsSeedVersion = "ot-reservation-v1";
  }
  if (prevCreative.kayaReservationLeadsSeedVersion === "ot-reservation-leads-v2") {
    creativePerformance.kayaReservationLeadsSeedVersion = "ot-reservation-leads-v2";
  }
  if (prevCreative.gaffleDateComparisonSeedVersion === "gaffle-date-comparison-v1") {
    creativePerformance.gaffleDateComparisonSeedVersion = "gaffle-date-comparison-v1";
  }
  const next: Record<string, unknown> = { ...prev, creativePerformance };
  if (typeof adLauncher === "boolean") next.enableAdLauncher = adLauncher;
  return next;
}

// Account ids come from the analytics /creative/accounts endpoint (Meta-style
// ids like "act_123..."). Keep the charset tight so a stored config can never
// smuggle querystring structure into the upstream URL.
export function isValidCreativeAccountId(id: unknown): id is string {
  return typeof id === "string" && /^[A-Za-z0-9_:-]{1,64}$/.test(id);
}

// ---------------------------------------------------------------------------
// Missing-day coverage detection (Task #2465).
//
// Compares the set of dates that appear in the trend feed against the selected
// [since, until] range and returns the TRUE gaps — days inside the window that
// have no trend rows and are not attributable to expected freshness lag.
//
// Excluded categories (must NOT trigger the warning):
//   1. Days before the feed start (already clamped by the date picker).
//   2. Trailing days when the range is RECENT (ends today or yesterday):
//      days after the latest trend date may simply be in-flight lag from the
//      feed mirror and are already surfaced separately via the freshness note.
//      Historical ranges (until < yesterday) get no trailing exclusion —
//      a window that closed a week ago has no excuse for trailing gaps.
//
// If the feed returned zero rows at all, no spans are returned (the existing
// empty-state UI already handles that case).
// ---------------------------------------------------------------------------

/** A contiguous span of missing days (both ends inclusive, YYYY-MM-DD). */
export interface MissingDaySpan {
  start: string;
  end: string;
}

/**
 * Return the contiguous spans of days inside [since, until] that have no
 * trend rows. Trailing lag (days after the latest trend date) is excluded
 * only when the requested range ends on a recent date (today or yesterday),
 * meaning the window is still in the expected freshness-lag zone.
 * Historical windows report ALL missing days.
 *
 * @param today - Override today's date (YYYY-MM-DD, UTC) for testability.
 *                Defaults to the current UTC date.
 */
export function detectMissingDaySpans(
  trendPoints: CreativeTrendPoint[],
  since: string,
  until: string,
  today?: string,
): MissingDaySpan[] {
  if (trendPoints.length === 0) return []; // no data yet → empty state, not a gap

  // Collect unique dates that have trend data.
  const trendDates = new Set<string>();
  let latestTrendDate = "";
  for (const p of trendPoints) {
    if (p.date) {
      trendDates.add(p.date);
      if (p.date > latestTrendDate) latestTrendDate = p.date;
    }
  }
  if (!latestTrendDate) return [];

  // Determine whether the selected window extends to a recent date (today or
  // yesterday). Only recent windows get trailing-lag exemption — historical
  // windows (until < yesterday) report all missing days as true gaps.
  const todayStr = today ?? new Date().toISOString().slice(0, 10);
  const yesterdayStr = new Date(new Date(todayStr + "T00:00:00Z").getTime() - 86_400_000)
    .toISOString().slice(0, 10);
  const rangeIsRecent = until >= yesterdayStr;

  // Coverage window: for recent ranges we stop at latestTrendDate (trailing
  // days past this point are plausible lag); for historical ranges we scan
  // the full [since, until] window — any gap there is a real hole.
  const coverageEnd = rangeIsRecent
    ? (until < latestTrendDate ? until : latestTrendDate)
    : until;

  if (since > coverageEnd) return []; // entire range is trailing lag (recent) or covered

  // Walk every calendar day in [since, coverageEnd] and collect missing ones.
  const missing: string[] = [];
  const cursor = new Date(since + "T00:00:00Z");
  const endMs = new Date(coverageEnd + "T00:00:00Z").getTime();
  while (cursor.getTime() <= endMs) {
    const ds = cursor.toISOString().slice(0, 10);
    if (!trendDates.has(ds)) missing.push(ds);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  if (missing.length === 0) return [];

  // Collapse consecutive calendar days into spans.
  const spans: MissingDaySpan[] = [];
  let spanStart = missing[0];
  let spanEnd = missing[0];
  for (let i = 1; i < missing.length; i++) {
    const prev = new Date(spanEnd + "T00:00:00Z");
    const curr = new Date(missing[i] + "T00:00:00Z");
    if ((curr.getTime() - prev.getTime()) / 86_400_000 === 1) {
      spanEnd = missing[i]; // extend current span
    } else {
      spans.push({ start: spanStart, end: spanEnd });
      spanStart = missing[i];
      spanEnd = missing[i];
    }
  }
  spans.push({ start: spanStart, end: spanEnd });
  return spans;
}

/** Format a single MissingDaySpan for display, e.g. "Jul 3", "Jul 3–5", "Jul 30–Aug 2". */
export function formatMissingDaySpan({ start, end }: MissingDaySpan): string {
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const fmt = (d: string) => {
    const [, mm, dd] = d.split("-");
    return `${MONTHS[parseInt(mm, 10) - 1]} ${parseInt(dd, 10)}`;
  };
  if (start === end) return fmt(start);
  const [sy, sm] = start.split("-");
  const [ey, em, ed] = end.split("-");
  if (sy === ey && sm === em) {
    // Same month — share the month label: "Jul 3–5"
    return `${fmt(start)}–${parseInt(ed, 10)}`;
  }
  return `${fmt(start)}–${fmt(end)}`;
}
